// The files on a payment request: a petrol slip, a proof of payment, a payslip.
// db/migrations/8000 and 8005.
//
// Upload is three steps and each one is the database's to refuse: a signed URL
// for a path the Worker composes, the bytes straight to storage, then a record
// that Postgres checks against storage.objects. Nothing can be deleted here,
// because a file on a payment request is the record of money going out.
import { useRef, useState } from 'react'
import { ExternalLink, Paperclip, Upload } from 'lucide-react'
import { Button } from '../../lib/shadcn/button'
import { Spinner } from '../../lib/shadcn/spinner'
import { useGetPaymentFileUrl, useRecordPaymentFile, useSignPaymentFileUpload } from '../../hooks/backend/payments'
import { DOCUMENT_ACCEPT, checkDocument, uploadSigned, type Signed } from '../../utils/signedUpload'
import { formatWhen } from '../../utils/loans'

export type PaymentFileKind = 'petrol' | 'online' | 'shortpay'
export type PaymentFileRole = 'receipt' | 'proof' | 'evidence'

export type PaymentFile = {
  id: string; role: PaymentFileRole; filename: string; mime_type: string | null
  uploaded_at: string; uploaded_by_name: string | null
}

export const FILE_ROLE_LABEL: Record<PaymentFileRole, string> = {
  receipt: 'Fuel slip',
  proof: 'Proof of payment',
  evidence: 'Supporting document',
}

/** Uploads one file to one request and records it. Throws the operator's sentence. */
export function usePaymentFileUpload() {
  const sign = useSignPaymentFileUpload<Signed>()
  const record = useRecordPaymentFile<{ ok: boolean; id: string | null }>()
  const [busy, setBusy] = useState(false)
  async function upload(kind: PaymentFileKind, requestId: string, role: PaymentFileRole, file: File) {
    const bad = checkDocument(file)
    if (bad) throw new Error(bad)
    setBusy(true)
    try {
      const signed = await sign.trigger({ kind, request_id: requestId, filename: file.name })
      const path = await uploadSigned(signed, file)
      await record.trigger({
        kind, request_id: requestId, role, storage_path: path,
        filename: file.name, mime_type: file.type || null, size_bytes: file.size,
      })
    } finally {
      setBusy(false)
    }
  }
  return { upload, busy }
}

export function PaymentFiles({ kind, requestId, files, uploadRoles, onChanged }: {
  kind: PaymentFileKind
  requestId: string
  files: PaymentFile[]
  /** The buttons to show, in order. Empty for a read-only list. */
  uploadRoles: { role: PaymentFileRole; label: string }[]
  onChanged: () => void
}) {
  const open = useGetPaymentFileUrl<{ url: string }>()
  const { upload, busy } = usePaymentFileUpload()
  const [error, setError] = useState<string | null>(null)
  const [pendingRole, setPendingRole] = useState<PaymentFileRole | null>(null)
  const input = useRef<HTMLInputElement>(null)

  // Opened before the await so the browser treats it as the click's own window.
  async function openFile(id: string) {
    setError(null)
    const w = window.open('', '_blank')
    try {
      const res = await open.trigger({ id }, { skipCache: true })
      if (res?.url && w) w.location.href = res.url
      else w?.close()
    } catch (e) {
      w?.close()
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  async function chosen(file: File | undefined) {
    if (!file || !pendingRole) return
    setError(null)
    try {
      await upload(kind, requestId, pendingRole, file)
      onChanged()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setPendingRole(null)
      if (input.current) input.current.value = ''
    }
  }

  return (
    <div className="space-y-1">
      {files.length > 0 && (
        <ul className="space-y-1">
          {files.map((f) => (
            <li key={f.id} className="flex flex-wrap items-center gap-2 text-xs">
              <Button size="sm" variant="outline" className="h-7" disabled={open.loading} onClick={() => void openFile(f.id)}>
                <ExternalLink className="mr-1 h-3.5 w-3.5" /> {f.filename}
              </Button>
              <span className="text-muted-foreground">
                {FILE_ROLE_LABEL[f.role]}, {formatWhen(f.uploaded_at)}{f.uploaded_by_name ? ` by ${f.uploaded_by_name}` : ''}
              </span>
            </li>
          ))}
        </ul>
      )}
      {uploadRoles.length > 0 && (
        <div className="flex flex-wrap items-center gap-1">
          <input ref={input} type="file" accept={DOCUMENT_ACCEPT} className="hidden"
            onChange={(e) => void chosen(e.target.files?.[0])} />
          {uploadRoles.map((u) => (
            <Button key={u.role} size="sm" variant="outline" className="h-7 bg-background" disabled={busy}
              onClick={() => { setPendingRole(u.role); input.current?.click() }}>
              {busy && pendingRole === u.role ? <Spinner className="mr-1 h-3.5 w-3.5" /> : u.role === 'proof' ? <Paperclip className="mr-1 h-3.5 w-3.5" /> : <Upload className="mr-1 h-3.5 w-3.5" />}
              {u.label}
            </Button>
          ))}
        </div>
      )}
      {error && <p className="rounded-md bg-rose-50 px-3 py-2 text-xs text-rose-800 dark:bg-rose-950 dark:text-rose-200">{error}</p>}
    </div>
  )
}
